<template>
  <div class="filter-wrapper">
    <div class="filter-dropdowns">
      <MultilayerSingleSelect
        v-model="localSelectedKey"
        class="key-dropdown"
        name="filter name"
        label="Filter Name"
        :optionsList="optionsList"
        @input="updateFilterKey(localSelectedKey)"
      >
        <template v-slot:mikmakShopperIntelligence>
          <PersonIcon class="section-icon"/>
          mikmak shopper intelligence
        </template>
        <template v-slot:mikmakInsights>
          <BarChartIcon class="section-icon"/>
          mikmak insights
        </template>
      </MultilayerSingleSelect>
      <RadioButtonDropdown
        v-model="localSelectedOperator"
        class="value-dropdown"
        :disabled="valueDropdownDisabled"
        :required="valueDropdownRequired"
        :filters="valuesOptions"
        :dimensionName="localSelectedKey"
        :isFetching="isFetching"
        @submit="updateFilterValue($event)"
        @updateFiltersIfNeed="updateFiltersIfNeed()"
      />
    </div>
    <ClearIcon
      class="clear-icon"
      @click.native="$emit('remove')"
    />
  </div>
</template>
<script>
import {
  PersonIcon,
  BarChartIcon,
  ClearIcon,
} from '@mikmak/components';
import MultilayerSingleSelect from '../../../../../Common/MultilayerSingleSelect.vue';
import RadioButtonDropdown from '../../../../../Common/RadioButtonDropdown.vue';
import { format, parseISO } from 'date-fns';
import { CommonLogger } from '../../../../../../../services/logger';
import { getDashboardFilterValues, getDemographicsFilterValues }
  from '../../../../../../../services/dashboard/eventApiService';

export default {
  props: {
    filter: {
      type: Object,
    },
    optionsList: {
      type: Array,
    },
    dateRange: {
      type: Object,
    },
    brandIds: {
      type: Array,
    },
  },
  data() {
    return {
      localSelectedKey: this.filter.key,
      localSelectedOperator: this.filter.operator,
      localSelectedValue: this.filter.value,
      isFetching: false,
      valuesOptions: [],
      allValues: false,
    };
  },
  computed: {
    hasBrandError() {
      return this.brandIds && this.brandIds.length === 0;
    },
    valueDropdownDisabled() {
      return !this.localSelectedKey.length || this.isFetching;
    },
    valueDropdownRequired() {
      return !!this.localSelectedKey.length;
    },
    startDate() {
      return this.formatDate(this.dateRange.start);
    },
    endDate() {
      return this.formatDate(this.dateRange.end);
    },
    brandsIdStr() {
      return encodeURIComponent(JSON.stringify(this.brandIds));
    },
  },
  methods: {
    updateFilterKey(value) {
      if (this.hasBrandError) return;

      this.valuesOptions = [];
      this.localSelectedKey = value;
      this.localSelectedOperator = '';
      this.localSelectedValue = [];
      this.updateFilter();
      this.getDimensionValues(value);
    },
    updateFilterValue(value) {
      this.localSelectedOperator = value.operator;
      this.localSelectedValue = value.filters;
      this.updateFilter();
    },
    updateFilter() {
      const newFilter = {
        key: this.localSelectedKey,
        operator: this.localSelectedOperator,
        value: this.localSelectedValue,
      };

      this.$emit('updateFilter', newFilter);
    },
    updateFiltersIfNeed() {
      if (this.localSelectedKey && !this.allValues)
        this.getDimensionValues(this.localSelectedKey);
    },
    async getDimensionValues(value) {
      this.isFetching = true;

      try {
        const dimension = this.optionsList.find(dim => dim.value === value);
        const token = await this.$auth.getTokenSilently();
        const payload = {
          brandIds: this.brandsIdStr,
          dimension: dimension.value,
          startDate: this.startDate,
          endDate: this.endDate,
        };

        if (dimension.section === 'mikmakInsights') {
          payload.activeView = '';

          const { data } = await getDashboardFilterValues(payload, token);

          this.setFilters(dimension, data);
        } else {

          const { filters }  = await getDemographicsFilterValues(payload, token);

          const data = filters.map(f => {
            const keyValue = {};

            keyValue[dimension.value] = f;

            return keyValue;
          });

          this.setFilters(dimension, data);
        }

      } catch (error) {
        CommonLogger.error('Error retrieving dashboard filter values', {
          error,
        });
      } finally {
        this.isFetching = false;
      }
    },
    setFilters(dimension, values) {
      this.valuesOptions = values
        ? values.map(dv => {
          const snakeVal = dv[dimension.snake];
          const valueVal = dv[dimension.value];

          const val = snakeVal === undefined || snakeVal === null ? valueVal : snakeVal;

          const isUndefinedOrNull = val === undefined || val === null;
          const valKey = isUndefinedOrNull ? 'null' : val.toString();
          const value = isUndefinedOrNull ? 'null' : val;

          const index = this.localSelectedValue
            .findIndex(filter => filter === value && value);

          return {
            key: valKey,
            value: value,
            checked: index >= 0,
          };
        })
        : [];
      this.allValues = true;
    },
    preSetFilters() {
      this.valuesOptions = this.localSelectedValue.map(val => {
        return {
          key: val,
          value: val,
          checked: true,
        };
      });
    },
    formatDate(date) {
      return format(parseISO(date), 'yyyy-MM-dd');
    },
  },
  watch: {
    filter() {
      this.localSelectedKey = this.filter.key;
      this.localSelectedOperator = this.filter.operator;
      this.localSelectedValue = this.filter.value;

      if (!this.localSelectedKey &&
        !this.localSelectedOperator &&
        this.localSelectedValue.length === 0) {
        this.valuesOptions = [];
      }
    },
  },
  mounted() {
    if (this.localSelectedKey && this.localSelectedValue)
      this.preSetFilters();
  },
  components: {
    PersonIcon,
    BarChartIcon,
    ClearIcon,
    MultilayerSingleSelect,
    RadioButtonDropdown,
  },
};
</script>
<style scoped>
.filter-wrapper {
  padding-bottom: 2px;
}
.filter-dropdowns {
  width: 100%;
  display: flex;
}
.key-dropdown {
  height: fit-content;
}
.key-dropdown, .value-dropdown{
  width: calc(50% - 16px);
  margin-right: 16px;
}
.clear-icon {
  padding-bottom: 20px;
  cursor: pointer;
}
</style>
